import _ from "lodash";
import React, { memo, useMemo } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import { colors } from "Constants";
import { useI18n } from "Hooks";
import { getCommentDisplayContent } from "Utils/RegexHelper";
import { highlightTextOutsideAnchorTag } from "Utils/highlightText";
import {
  ATTACHMENT_TYPES,
  MESSAGE_STATUS,
  getMentionsHtml,
  renderLinkInMessage,
  areMomentsSame,
} from "Utils/MessageHelpers";
import MultiTypeAttachments from "../MultiTypeAttachments";
import {
  DoubleVisualMediaContainer,
  GridVisualMediaContainer,
  SingleVisualMediaContainer,
} from "../VisualMedia";
import ParentMessage from "../ParentMessage";

import classes from "./MessageBubble.scss";
import { getStyleStrippedText } from "Utils";

const IMAGE_OR_VIDEO_TYPES = [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO];

export const Attachments = props => {
  const { totalAttachments, attachments, onMediaClick } = props;

  const isOnlyImagesOrVideos = _.every(attachments, singleAttachment =>
    _.includes(IMAGE_OR_VIDEO_TYPES, singleAttachment?.type)
  );

  if (isOnlyImagesOrVideos) {
    switch (totalAttachments) {
      case 1:
        return (
          <SingleVisualMediaContainer
            attachment={_.first(attachments)}
            onMediaClick={onMediaClick}
          />
        );
      case 2:
        return (
          <DoubleVisualMediaContainer
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        );
      // Is this case needed? For smaller screens It does not look good.
      // We can just render it using GridVisualMediaContainer which looks good on all screens.

      // case 3:
      //   return (
      //     <TripleVisualMediaContainer
      //       attachments={attachments}
      //       onMediaClick={onMediaClick}
      //     />
      //   );
      default:
        return (
          <GridVisualMediaContainer
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        );
    }
  }

  return (
    <MultiTypeAttachments
      attachments={attachments}
      onMediaClick={onMediaClick}
    />
  );
};

Attachments.propTypes = {
  totalAttachments: PropTypes.number.isRequired,
  attachments: PropTypes.array.isRequired,
  onMediaClick: PropTypes.func.isRequired,
};

const MessageLabel = ({
  label,
  searchText,
  isEditedMessage = false,
  renderLinksAsAnchor = true,
}) => {
  const { t } = useI18n();
  const labelClasses = classNames(
    "text-body-2",
    "text-textDefault",
    classes.message
  );
  const editedMessageClass = classNames("text-body-3", classes.editedMessage);

  const parsedLabel = useMemo(() => {
    const linkedLabel = getCommentDisplayContent(label, {
      getHtml: getMentionsHtml,
      renderLinksAsAnchor,
      autoLinkReplaceFn: match => {
        switch (match.getType()) {
          case "url":
            return renderLinkInMessage({ match, searchText });
          default:
            // return true to let the Autolinker(library) continue
            return true;
        }
      },
    });

    return searchText
      ? highlightTextOutsideAnchorTag({
          item: linkedLabel,
          keyword: searchText,
        })
      : linkedLabel;
  }, [label, searchText, renderLinksAsAnchor]);

  return (
    <>
      <span
        className={labelClasses}
        dangerouslySetInnerHTML={{
          __html: getStyleStrippedText({
            text: parsedLabel,
            type: "SANITIZE_DANGER_HTML_PARAGRAPH",
          }),
        }}
      />
      {isEditedMessage ? (
        <span className={editedMessageClass}>{`(${t(
          "integration:edited"
        )})`}</span>
      ) : null}
    </>
  );
};

const MessageBubble = props => {
  const {
    openPreviewModal,
    message,
    isSender,
    status,
    onParentMessageClick,
    searchText,
    renderLinksAsAnchor = true,
  } = props;

  const {
    label,
    parentMessage,
    attachments,
    createdAt,
    updatedAt,
    item,
  } = message;
  const messageAttachments = attachments || _.get(item, "attachments", []);

  const { t } = useI18n();

  const isSent = !status;
  const isEditedMessage =
    isSent &&
    !!updatedAt &&
    !areMomentsSame({ date1: createdAt, date2: updatedAt });

  const totalAttachments = _.size(messageAttachments);

  const messageBubbleClassesV2 = classNames(classes.messageBubbleContainerV2, {
    [classes.bubbleWithAttachments]: totalAttachments > 0,
  });

  const messageStatusFooterClasses = classNames(classes.messageStatusFooter, {
    [classes.absoluteFooter]: !!label,
  });
  const editedMessageClass = classNames("text-body-3", classes.editedMessage);

  const onMediaClick = selectedAttachment => {
    // if message is in sending or failed state, don't open preview modal
    if (!isSent) return;
    openPreviewModal({ attachments: messageAttachments, selectedAttachment });
  };

  const style = {
    "--background-color": isSender ? colors.surfaceLink : colors.surfaceDefault,
    "--highlight-color": isSender ? colors.blue900 : colors.surfaceDefaultHover,
  };
  return (
    <div style={style} className={messageBubbleClassesV2}>
      {parentMessage ? (
        <ParentMessage
          isChildMessageSentByUser={isSender}
          parentMessage={parentMessage}
          goToParentMessage={onParentMessageClick}
        />
      ) : null}

      {label ? (
        <div className={classes.messageLabel}>
          <MessageLabel
            label={label}
            isEditedMessage={isEditedMessage}
            searchText={searchText}
            renderLinksAsAnchor={renderLinksAsAnchor}
          />
        </div>
      ) : null}

      {totalAttachments ? (
        <div className={classes.messageAttachmentContainer}>
          <Attachments
            totalAttachments={totalAttachments}
            attachments={messageAttachments}
            onMediaClick={onMediaClick}
          />
        </div>
      ) : null}

      {!label && totalAttachments && isEditedMessage ? (
        <div className={editedMessageClass}>{`(${t(
          "integration:edited"
        )})`}</div>
      ) : null}

      {status !== MESSAGE_STATUS.FAILED ? (
        <div className={messageStatusFooterClasses}></div>
      ) : null}
    </div>
  );
};

MessageBubble.propTypes = {
  openPreviewModal: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  isSender: PropTypes.bool.isRequired,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
  onParentMessageClick: PropTypes.func.isRequired,
  searchText: PropTypes.string,
  renderLinksAsAnchor: PropTypes.bool,
};

export default memo(MessageBubble);
